<template>
  <div class="f-row">
    <div class="row f-row justify-content-center mt-2">
      <div class="col col-lg-12 text-center progress-div" v-if="showLoading">
        <v-progress-circular
          indeterminate
          :width="3"
          color="primary"
        ></v-progress-circular>
      </div>
      <div class="col col-11">
        <v-data-table
          dense
          :dark="colorTheme"
          hide-default-footer
          fixed-header
          :headers="tableHeaders"
          :items-per-page="1000"
          @page-count="pageCount = $event"
          :items="tableData"
          :height="screenHeight"
        >
          <template v-slot:item.observation_date="{ item }"
            ><v-chip>{{ item.observation_date | dateFormat }}</v-chip></template
          >
          <template v-slot:item.open="{ item }"
            ><v-chip>{{ item.open | numberFormat }}</v-chip></template
          >
          <template v-slot:item.high="{ item }"
            ><v-chip>{{ item.high | numberFormat }}</v-chip></template
          >
          <template v-slot:item.low="{ item }"
            ><v-chip>{{ item.low | numberFormat }}</v-chip></template
          >
          <template v-slot:item.close="{ item }"
            ><v-chip>{{ item.close | numberFormat }}</v-chip></template
          >
          <template v-slot:item.settle="{ item }"
            ><v-chip>{{ item.settle | numberFormat }}</v-chip></template
          >
          <template v-slot:item.volumes="{ item }"
            ><v-chip>{{ item.volumes | numberFormat }}</v-chip></template
          >
        </v-data-table>
      </div>
    </div>
  </div>
</template>

<script>
import { getCommodityTableData } from "@/_core/data/APICalls";
import { getUnits } from "@/_core/data/Markets";
import moment from "moment";
import { format, timeFormat, timeParse } from "d3";

export default {
  name: "PriceTable",
  props: {
    colorTheme: { required: true, type: Boolean, default: true },
    commodityNumber: { required: true },
    commoditySpecs: { required: true }
    // ============ Data Classes ===============
  },
  data() {
    return {
      screenHeight: 0,
      showLoading: false,
      tableDataRaw: [],
      tableData: [],
      commodityNames: [],
      commodityById: {},
      maxContractsPerCommodity: 6,
      allCommodities: {
        0: "aluminium",
        1: "brent",
        2: "canola oil",
        3: "cocoa",
        4: "coffee",
        5: "copper",
        6: "corn",
        7: "cotton",
        8: "cpo cif rott",
        9: "cpo mal myr",
        10: "ethanol",
        11: "gold",
        12: "ho",
        13: "natgas",
        14: "nickel",
        15: "pko",
        16: "platinum",
        17: "rbob",
        18: "rubber",
        19: "silver",
        20: "soybean",
        21: "soyoil",
        22: "soyoil arg",
        23: "soyoil usa",
        24: "stearin",
        25: "sugar",
        26: "tallow",
        27: "wheat hrs",
        28: "wheat hrw",
        29: "wheat srw",
        30: "wti",
        31: "zinc",
        32: "soymeal",
        33: "mb propane",
        34: "cpo mal usd",
        35: "lead"
      },
      commodityList: [],
      tableHeaders: [
        { text: "Commodity", value: "fk_commodities", sortable: true },
        { text: "Date", value: "observation_date", sortable: true },
        { text: "Units", value: "units", sortable: true },
        { text: "Open", value: "open", sortable: true },
        { text: "High", value: "high", sortable: true },
        { text: "Low", value: "low", sortable: true },
        { text: "Close", value: "close", sortable: true },
        { text: "Settle", value: "settle", sortable: true },
        { text: "Volume", value: "volumes", sortable: true },
        { text: "Contract", value: "contract_month_year", sortable: true }
      ]
    };
  },
  filters: {
    numberFormat: function(val) {
      return val === null || val === 0 ? "-" : format(",.2f")(val);
    },
    dateFormat: function(val) {
      return val === null || val === 0
        ? "-"
        : timeFormat("%d %b %y")(timeParse("%Y-%m-%d")(val));
    }
  },
  created() {
    this.showLoading = true;
    let _this = this;
    window.addEventListener("resize", function() {
      _this.callWidth();
    });
  },
  mounted() {
    this.screenHeight =
      document.getElementById("whole-page-div").clientHeight - 75;
    let _this = this;
    this.commodityNames = this.commoditySpecs.map(d => d.fk_idproduct);
    this.commodityById = {};
    this.commoditySpecs.forEach(function(d) {
      _this.commodityById[d.pk_idcommodity] = d.fk_idproduct;
    });
    this.getData();
  },
  watch: {
    commodityNumber() {
      this.tableDataRaw = [];
      this.tableData = [];
      this.showLoading = true;
      this.getData();
    },
    showLoading(val) {
      if (val === false) {
        this.tableData.forEach(function(d) {
          d.observation_date =
            d.observation_date === null
              ? ""
              : moment(d.observation_date).format("YYYY-MM-DD");
        });
      }
    }
  },
  methods: {
    getData() {
      let _this = this;
      this.listByCommodity(this.commodityNumber);
      this.commodityNumberList = [];
      let idx = [];
      this.commodityList.forEach(function(d) {
        idx.push(_this.commodityNames.indexOf(d));
        _this.commodityNumberList.push(
          _this.commoditySpecs[_this.commodityNames.indexOf(d)].pk_idcommodity
        );
      });
      let units = [];
      idx.forEach(function(_, i) {
        units.push(getUnits(idx[i], _this.commoditySpecs));
      });
      getCommodityTableData(
        this,
        "tableData",
        "showLoading",
        this.commodityNumberList,
        units
      );
    },
    listByCommodity(idx) {
      let _this = this;
      let list;
      if (this.commodityById[idx] === "aluminium") {
        list = [0, 5, 14, 31, 35, 11, 19, 16];
      } else if (this.commodityById[idx] === "brent") {
        list = [1, 30, 10, 12, 13, 17, 33];
      } else if (this.commodityById[idx] === "canola oil") {
        list = [2, 9, 8, 15, 21, 30, 1, 24, 22, 23];
      } else if (this.commodityById[idx] === "cocoa") {
        list = [3, 4, 6, 7, 20, 25, 27, 28, 29, 32];
      } else if (this.commodityById[idx] === "coffee") {
        list = [4, 3, 6, 7, 20, 25, 27, 28, 29, 32];
      } else if (this.commodityById[idx] === "copper") {
        list = [5, 0, 14, 31, 35, 11, 19, 16];
      } else if (this.commodityById[idx] === "corn") {
        list = [6, 3, 4, 7, 20, 25, 27, 28, 29, 32];
      } else if (this.commodityById[idx] === "cotton") {
        list = [3, 4, 6, 20, 25, 27, 28, 29, 32];
      } else if (this.commodityById[idx] === "cpo cif rott") {
        list = [8, 34, 9, 15, 21, 22, 23, 24, 26, 2, 1, 30];
      } else if (this.commodityById[idx] === "cpo mal myr") {
        list = [9, 8, 34, 15, 21, 22, 23, 24, 26, 2, 1, 30];
      } else if (this.commodityById[idx] === "ethanol") {
        list = [10, 1, 30, 12, 13, 17, 33];
      } else if (this.commodityById[idx] === "gold") {
        list = [11, 19, 16, 0, 5, 14, 31, 35];
      } else if (this.commodityById[idx] === "ho") {
        list = [12, 1, 30, 10, 13, 17, 33];
      } else if (this.commodityById[idx] === "natgas") {
        list = [13, 33, 1, 30, 10, 12, 17];
      } else if (this.commodityById[idx] === "nickel") {
        list = [14, 0, 5, 31, 35, 11, 19, 16];
      } else if (this.commodityById[idx] === "pko") {
        list = [15, 9, 8, 34, 21, 22, 23, 24, 26, 2, 1, 30];
      } else if (this.commodityById[idx] === "platinum") {
        list = [16, 11, 19, 0, 5, 14, 31, 35];
      } else if (this.commodityById[idx] === "rbob") {
        list = [17, 1, 30, 10, 12, 13, 33];
      } else if (this.commodityById[idx] === "rubber") {
        list = [18, 1, 30, 11, 19];
      } else if (this.commodityById[idx] === "silver") {
        list = [19, 11, 16, 0, 5, 14, 31, 35];
      } else if (this.commodityById[idx] === "soybean") {
        list = [20, 32, 3, 4, 6, 7, 25, 27, 28, 29];
      } else if (this.commodityById[idx] === "soyoil") {
        list = [21, 22, 23, 8, 34, 9, 15, 24, 26, 2, 1, 30];
      } else if (this.commodityById[idx] === "soyoil arg") {
        list = [22, 21, 23, 8, 34, 9, 15, 24, 26, 2, 1, 30];
      } else if (this.commodityById[idx] === "soyoil usa") {
        list = [23, 21, 22, 8, 34, 9, 15, 24, 26, 2, 1, 30];
      } else if (this.commodityById[idx] === "stearin") {
        list = [24, 9, 8, 34, 15, 21, 22, 23, 26, 2, 1, 30];
      } else if (this.commodityById[idx] === "sugar") {
        list = [25, 3, 4, 6, 7, 20, 27, 28, 29, 32];
      } else if (this.commodityById[idx] === "tallow") {
        list = [26, 9, 8, 34, 15, 21, 22, 23, 24, 2, 1, 30];
      } else if (this.commodityById[idx] === "wheat hrs") {
        list = [27, 28, 29, 3, 4, 6, 7, 20, 25, 32];
      } else if (this.commodityById[idx] === "wheat hrw") {
        list = [28, 27, 29, 3, 4, 6, 7, 20, 25, 32];
      } else if (this.commodityById[idx] === "wheat srw") {
        list = [29, 27, 28, 3, 4, 6, 7, 20, 25, 32];
      } else if (this.commodityById[idx] === "wti") {
        list = [30, 1, 10, 12, 13, 17, 33];
      } else if (this.commodityById[idx] === "zinc") {
        list = [31, 0, 5, 14, 35, 11, 19, 16];
      } else if (this.commodityById[idx] === "soymeal") {
        list = [32, 20, 3, 4, 6, 7, 25, 27, 28, 29];
      } else if (this.commodityById[idx] === "mb propane") {
        list = [33, 1, 30, 10, 12, 13, 17];
      } else if (this.commodityById[idx] === "cpo mal usd") {
        list = [34, 9, 8, 21, 20, 30, 1, 24, 26, 22, 23, 2];
      } else if (this.commodityById[idx] === "lead") {
        list = [35, 0, 5, 14, 31, 11, 19, 16];
      } else {
        list = [1, 30, 11, 19];
      }
      this.commodityList = [];
      list.forEach(function(d) {
        _this.commodityList.push(_this.allCommodities[d]);
      });
    },
    callWidth() {
      this.screenHeight =
        document.getElementById("whole-page-div").clientHeight - 75;
    }
  }
};
</script>

<style scoped></style>
